import { forwardRef, useState, type ChangeEvent, type FocusEvent } from "react"
import { useTranslation } from "react-i18next"

import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

type InputProps = React.ComponentPropsWithoutRef<typeof Input>

interface DecimalInputProps extends Omit<InputProps, "type" | "onChange" | "value"> {
  value: number | string | null | undefined
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
}

// DecimalInput accepts both "," and "." as decimal separator (so French
// analysts can type "8,20" and English ones "8.20") and emits a
// normalised string with "." to the parent. Display follows the active
// i18n locale: French shows "8,20", English shows "8.20".
//
// type="text" with inputMode="decimal" keeps the mobile decimal keypad
// available while letting us control the displayed separator.
export const DecimalInput = forwardRef<HTMLInputElement, DecimalInputProps>(
  function DecimalInput({ value, onChange, onBlur, className, ...rest }, ref) {
    const { i18n } = useTranslation()
    const isFr = (i18n.language || "").toLowerCase().startsWith("fr")
    const localSep = isFr ? "," : "."

    const toDisplay = (v: number | string | null | undefined) => {
      if (v == null || v === "") return ""
      const s = String(v)
      return isFr ? s.replace(".", ",") : s.replace(",", ".")
    }

    const [display, setDisplay] = useState<string>(() => toDisplay(value))

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value
      // Allow digits, both separators (we normalise below), and an
      // optional leading minus — even if the user types the "wrong"
      // separator we accept it without surprises.
      const cleaned = raw.replace(/[^\d.,-]/g, "")
      setDisplay(cleaned)
      const normalised = cleaned.replace(",", ".")
      const synthetic = {
        ...event,
        target: { ...event.target, value: normalised },
        currentTarget: { ...event.currentTarget, value: normalised },
      } as ChangeEvent<HTMLInputElement>
      onChange(synthetic)
    }

    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
      const normalised = display.replace(",", ".")
      const n = Number(normalised)
      if (!Number.isNaN(n) && display !== "") {
        const out = String(n)
        setDisplay(isFr ? out.replace(".", ",") : out)
      }
      onBlur?.(event)
    }

    return (
      <Input
        {...rest}
        ref={ref}
        type="text"
        inputMode="decimal"
        value={display}
        onChange={handleChange}
        onBlur={handleBlur}
        className={cn(className)}
        data-decimal-sep={localSep}
      />
    )
  },
)
